import type { FC } from 'react';

import { t } from '@nodal/i18n';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Checkbox } from '@nodal/uikit/components/Checkbox';
import { PhoneNumberInput } from '@nodal/uikit/components/PhoneNumberInput';
import { Select as PureSelect } from '@nodal/uikit/components/Select/Select';
import { TextInput } from '@nodal/uikit/components/TextInput';
import { useField } from 'formik';

import { states } from '@core/consts/states';
import { getOrdinalNumber } from '@core/utils';

import type { IvfTransferDetail } from './MedicalRecordReview.interface';

// Generate year options for transfer year selection
const currentYear = new Date().getFullYear();
const transferYears: { label: string; value: string }[] = [];
for (let i = currentYear; i >= 1990; i--) {
  transferYears.push({ label: i.toString(), value: `${i}-01-01` });
}

const IvfTransferDetailFields: FC<{
  detail: IvfTransferDetail;
  idx: number;
  editable: boolean;
}> = ({ detail, idx, editable }) => {
  const [transferDateField, transferDateMeta, transferDateHelpers] = useField(
    `ivf_transfers_details.${idx}.transfer_date`,
  );

  const [
    doctorClinicStateField,
    doctorClinicStateMeta,
    doctorClinicStateHelpers,
  ] = useField(`ivf_transfers_details.${idx}.doctor_clinic_state`);

  const transferDateValue = transferDateField.value
    ? (() => {
        // Extract year directly from the date string to avoid timezone issues
        const dateStr = transferDateField.value;
        const year = dateStr.split('-')[0];
        return `${year}-01-01`;
      })()
    : transferDateField.value === null
    ? undefined
    : transferDateField.value;

  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-10" key={idx}>
      <div className="relative">
        <div className="flex absolute inset-0 items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="flex relative justify-start">
          <span className="pr-3 text-base font-semibold leading-7 text-gray-900 bg-white">
            {t(
              '{numberWithOrdinal} IVF Transfer',
              `${getOrdinalNumber(idx + 1)}`,
            )}
          </span>
        </div>
      </div>
      <PureSelect
        label="Year of transfer"
        name={transferDateField.name}
        value={transferDateValue}
        onChange={(value) => transferDateHelpers.setValue(value)}
        error={
          transferDateMeta.touched && transferDateMeta.error
            ? transferDateMeta.error
            : undefined
        }
        options={transferYears}
        placeholder="YYYY"
        className="lg:w-1/2"
        disabled={!editable}
      />
      {/* {idx !== 0 && (
        <Checkbox
          name={`ivf_transfers_details.${idx}.same_doctor_details_as_first`}
          label={t('Same doctor details as first transfer')}
          disabled={!editable}
        />
      )} */}

      {(idx === 0 || !detail.same_doctor_details_as_first) && (
        <>
          <TextInput
            label={`Doctor's Name (optional)`}
            name={`ivf_transfers_details.${idx}.doctor_name`}
            type="text"
            placeholder="Doctor's Name"
            disabled={!editable}
          />
          <PhoneNumberInput
            label={`Doctor's Phone Number (optional)`}
            name={`ivf_transfers_details.${idx}.doctor_phone_number`}
            placeholder={'(555) 987-6543'}
            type="text"
            className="lg:w-1/2"
            disabled={!editable}
          />
          <TextInput
            label={t("Doctor's Clinic Name")}
            name={`ivf_transfers_details.${idx}.doctor_clinic_name`}
            type="text"
            placeholder="Doctor's Clinic Name"
            disabled={!editable}
          />
          <div className="grid grid-cols-1 gap-x-4 lg:grid-cols-2">
            <TextInput
              label={"Doctor's Clinic City"}
              name={`ivf_transfers_details.${idx}.doctor_clinic_city`}
              placeholder="Doctor's Clinic City"
              type="text"
              disabled={!editable}
            />
            <PureSelect
              label={"Doctor's Clinic State"}
              name={doctorClinicStateField.name}
              value={doctorClinicStateField.value}
              onChange={(value) => doctorClinicStateHelpers.setValue(value)}
              error={
                doctorClinicStateMeta.touched && doctorClinicStateMeta.error
                  ? doctorClinicStateMeta.error
                  : undefined
              }
              options={states}
              placeholder="Doctor's Clinic State"
              disabled={!editable}
            />
          </div>
        </>
      )}
    </div>
  );
};

// NOTE: The component must be embedded inside Formik
export const IvfTransferDetailsForm: FC<{
  ivfTransfersDetails: IvfTransferDetail[];
  editable: boolean;
}> = ({ ivfTransfersDetails, editable }) => {
  return (
    <>
      {ivfTransfersDetails?.map((detail: IvfTransferDetail, idx: number) => (
        <IvfTransferDetailFields
          key={idx}
          detail={detail}
          idx={idx}
          editable={editable}
        />
      ))}
    </>
  );
};
